'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import NavMarker from 'react-rhumb/NavMarker';
import { useLocation } from 'react-router-dom';
import { parse as parseParams } from 'hub-http/helpers/params';
import Raven from 'raven-js';
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Review from 'marketplace-ui-reviews/records/Review';
import ProfileReviewWizard from 'marketplace-ui-reviews/components/wizard/ProfileReviewWizard';
import getDisplayedReviewerName from 'marketplace-ui-reviews/utils/getDisplayedReviewerName';
import ProfileListing from 'ecosystem-marketplace-core/records/ProfileListing';
import { isPending, isFailed } from 'ecosystem-marketplace-core/utils/requestState';
import { getUserFirstName, getUserLastName, getUserEmail, getIsHubSpotter } from '../../selectors/auth';
import { submitProfileReview } from '../../actions/ReviewActions';
import { getReviewSubmitRequestState } from '../../selectors/reviews';
import { debug, isProd } from '../../utils/env';
import { push } from '../../utils/history';
import { getServices } from '../../slices/serviceCatalogSlice';
const I18N_BASE = 'ecosystem-marketplace-solutions-ui.profile.reviews';

const ReviewWizard = ({
  onConfirm,
  onClose,
  onTrackInteraction,
  profileListing
}) => {
  const {
    id: profileId,
    name,
    logoUrl,
    reviewability,
    slug
  } = profileListing;
  const dispatch = useDispatch();
  const reviewSubmitRequestState = useSelector(getReviewSubmitRequestState);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const userEmail = useSelector(getUserEmail);
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const isHubSpotter = useSelector(getIsHubSpotter);
  const catalogServices = useSelector(getServices);
  const {
    search
  } = useLocation();
  const parsedSearch = parseParams(search.slice(1));
  const reviewSource = parsedSearch['eco_review_source'] || 'details';
  const defaultReview = useMemo(() => {
    let storedReview = null;

    try {
      storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    } catch (e) {// pass through
    }

    return storedReview ? new Review(storedReview) : new Review();
  }, [slug]);
  const handleSubmit = useCallback(review => new Promise((resolve, reject) => {
    sessionStorage.removeItem(`${slug}_review`);
    const reviewToSubmit = review.updateReview({
      createdAt: new Date().valueOf(),
      entityId: profileId,
      reviewType: 'PROFILE',
      reviewerDisplayName: displayName
    });
    dispatch(submitProfileReview(reviewToSubmit)).then(() => {
      onTrackInteraction('submit review', {
        reviewSource
      });
      onConfirm();
      onClose();
      resolve('Successfully submitted review');
    }).catch(err => {
      Raven.captureMessage('Failed to leave a review', {
        level: 'error',
        extra: Object.assign({
          profileId
        }, err)
      });
      reject(err);
    });
  }), [slug, profileId, dispatch, displayName, onTrackInteraction, reviewSource, onConfirm, onClose]);
  const updateSessionStorage = useCallback(updatedReview => {
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', {
      nextStep,
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  useEffect(() => {
    if (!reviewability.isReviewable()) {
      const reason = reviewability.getUnreviewableReason();
      dispatch(postNotification({
        id: 'UNREVIEWABLE_PROFILE_NOTIFICATION',
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_BASE}.unreviewable.titleText`
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_BASE}.unreviewable.${reason}`
        })
      }));
      push(`/${slug}`);
    }
  }, [dispatch, reviewability, slug]);
  const isSubmitDisabled = useMemo(() => isHubSpotter && isProd() && !debug('hubspotter-review-prod'), [isHubSpotter]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: reviewability.isReviewable() && /*#__PURE__*/_jsx(ProfileReviewWizard, {
      catalogServices: catalogServices,
      defaultReview: defaultReview,
      displayName: displayName,
      disableSubmit: isSubmitDisabled,
      failed: isFailed(reviewSubmitRequestState),
      loading: isPending(reviewSubmitRequestState),
      reviewableName: name,
      reviewableIconUrl: logoUrl,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      submitTooltip: isSubmitDisabled && /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.unreviewable.hubspotter`
      }),
      userEmail: userEmail
    })
  });
};

ReviewWizard.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing)
};
export default ReviewWizard;